import { SyncIcon } from '@primer/octicons-react';
import { Box, FormControl, Label, Text as PrimerText, useConfirm } from '@primer/react';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { danThuocApi } from '../../api/danThuocApi';
import HocSinhShortInfo from '../../components/hoc-sinh/HocSinhShortInfo';
import Button from '../../components/ui/button';
import DataTable, { IColumn } from '../../components/ui/data-table/DataTable';
import Modal from '../../components/ui/modal';
import ModalActions from '../../components/ui/modal/ModalActions';
import Text from '../../components/ui/text';
import TextInput from '../../components/ui/text-input';
import TextWithEllipse from '../../components/ui/text-with-ellipse';
import { useCommonContext } from '../../contexts/common';
import { NotifyHelper } from '../../helpers/toast';
import { useCommonSelectedHook } from '../../hooks/useCommonSelectedHook';
import { useLanguage } from '../../hooks/useLanguage';
import { IFaDanThuoc } from '../../models/response/dan-thuoc/IDanThuoc';
import { IFaDanThuocItemRepsone } from '../../models/response/dan-thuoc/IDanThuocItemRepsone';


const DanThuocPage = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [dataSource, setDataSource] = useState<any[]>([]);
    const { translate } = useCommonContext();
    const { language } = useLanguage();
    const { dm_coso_id, nam_hoc } = useCommonSelectedHook();
    const [danThuocIdReject, setDanThuocIdReject] = useState(0);
    const [danThuocNote, setDanThuocNote] = useState("");
    const [isSaving, setIsSaving] = useState(false);
    const [previewImage, setPreviewImage] = useState<string | null>(null);

    // Filter states
    const [filterTuNgay, setFilterTuNgay] = useState<string>(moment().format('YYYY-MM-DD'));
    const [filterDenNgay, setFilterDenNgay] = useState<string>(moment().format('YYYY-MM-DD'));


    const confirm = useConfirm();
    useEffect(() => {
        handleReloadAsync();
    }, [])

    // Auto reload when filters change
    useEffect(() => {
        handleReloadAsync();
    }, [filterTuNgay, filterDenNgay, dm_coso_id, nam_hoc])
    const handleReloadAsync = async () => {
        setIsLoading(true);
        const res = await danThuocApi.selectByCoso({
            tu_ngay: filterTuNgay || null,
            den_ngay: filterDenNgay || null,
            dm_coso_id: dm_coso_id,
            nam_hoc: nam_hoc
        });
        setIsLoading(false)
        if (res.is_success) {
            setDataSource(res.data)
        } else {
            NotifyHelper.Error(res.message ?? "Có lỗi")
        }
    }
    const submitAcceptAsync = async (id: number, is_accept: boolean, note: string) => {
        setIsSaving(true)
        const res = await danThuocApi.accpetAsync({
            id,
            is_accept,
            note
        })
        setIsSaving(false)
        if (res.is_success) {
            NotifyHelper.Success("Success")
            handleReloadAsync();
            setDanThuocIdReject(0);
        } else {
            NotifyHelper.Error(res.message ?? "Error")
        }
    }
    const handleAcceptAsync = async (id: number, is_accept: boolean) => {
        if (is_accept) {
            if (await confirm({
                content: `${translate("DanThuocPage.Confirm.Accept.Content")}`,
                title: `${translate("DanThuocPage.Confirm.Accept.Title")}`,
                cancelButtonContent: `${translate("Base.Label.Cancel")}`,
                confirmButtonContent: `${translate("Base.Label.Confirm")}`,
                confirmButtonType: "primary",
            })) {
                submitAcceptAsync(id, true, "")
            }
        } else {
            setDanThuocIdReject(id)
        }
    }

    const columns = useMemo((): any => {
        let result: IColumn[] = [
            {
                id: "stats",
                width: "120px",
                caption: `${translate('Base.Label.Status')}`,
                isVisible: false,
                cellRender: (data: IFaDanThuocItemRepsone) => {

                    return (
                        <Box sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            textAlign: "center"
                        }}>
                            <Label variant={
                                data.fa_danthuoc_status_id === 3 ? "success" :
                                    (
                                        data.fa_danthuoc_status_id === 2 ? "danger" :
                                            "default"
                                    )
                            }>
                                {data.status_name || (data.fa_danthuoc_status_id === 0 ? (language === "en" ? "Created" : "Tạo mới") :
                                    data.fa_danthuoc_status_id === 2 ? (language === "en" ? "Rejected" : "Đã từ chối") :
                                    data.fa_danthuoc_status_id === 3 ? (language === "en" ? "Accepted" : "Đã đồng ý") : "N/A")}
                            </Label>
                        </Box>
                    );
                }
            },
            {
                id: "cmd",
                width: "150px",
                isVisible: false,
                caption: `${translate('Base.Label.Status')}`,
                cellRender: (data: IFaDanThuocItemRepsone) => {


                    return (
                        <Box sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            textAlign: "center"
                        }}>
                            {data.fa_danthuoc_status_id === 0 &&
                                <Box sx={{ display: "flex" }}>
                                    <Button text='Base.Label.Reject' variant='danger' size='small' onClick={() => {
                                        handleAcceptAsync(data.id, false)
                                    }} />
                                    <Button text='Base.Label.Accept' sx={{ ml: 2 }} variant='primary' size='small' onClick={() => {
                                        handleAcceptAsync(data.id, true)
                                    }} />
                                </Box>
                            }
                            {data.fa_danthuoc_status_id === 2 &&
                                <Box>
                                    {data.ghi_chu_xu_ly}
                                </Box>
                            }
                        </Box>
                    );
                }
            },
            {
                id: "trang_thai",
                caption: translate("Trạng thái"),
                width: 150,
                cellRender: (data: IFaDanThuocItemRepsone) => (
                    <Box sx={{ padding: 1 }}>
                        <PrimerText sx={{
                            fontSize: 1,
                            fontWeight: "500",
                        }}>
                            <TextWithEllipse text={data.trang_thai_hoc_sinh} lineNumber={1} />
                        </PrimerText>
                    </Box>
                ),
            },
            {
                id: "loai_dan_thuoc",
                caption: translate("Loại dặn thuốc"),
                width: 150,
                cellRender: (data: IFaDanThuocItemRepsone) => (
                    <Box sx={{ padding: 1 }}>
                        <PrimerText sx={{
                            fontSize: 1,
                            fontWeight: "500",
                        }}>
                            <TextWithEllipse text={data.type_ten || "N/A"} lineNumber={1} />
                        </PrimerText>
                    </Box>
                ),
            },
            {
                dataField: "ma_hs",
                width: 100,
                align: "center",
                caption: translate("Mã học sinh"),
                fixed: true
            },
            {
                id: "ho_ten",
                caption: translate("Họ tên"),
                width: 250,
                cellRender: (data: IFaDanThuocItemRepsone) => (
                    <HocSinhShortInfo
                        data={data}
                    />
                ),
            },
            {
                id: "thong_tin_truong_lop",
                caption: translate("Lớp - Khối - Hệ"),
                width: 200,
                cellRender: (data: IFaDanThuocItemRepsone) => {
                    return (
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "baseline",
                                // fontWeight: 600
                            }}
                        >
                            <Box sx={{ display: "flex" }}>
                                <Box sx={{ fontWeight: 600 }}>{data.ten_lop}</Box>
                            </Box>
                            <Box sx={{ color: "fg.muted" }}>
                                {data.ten_khoi}, Hệ {data.ten_he}
                            </Box>
                        </Box>
                    );
                },
            },
            {
                dataField: "tu_ngay",
                align: "center",

                width: "120px",
                caption: `${translate('Base.Label.FromDate')}`,
                cellRender: (data: IFaDanThuoc) => {
                    return <>
                        {moment(data.tu_ngay).format("DD/MM/YYYY")}
                    </>
                }
            },
            {
                dataField: "den_ngay", align: "center",

                width: "120px",
                caption: `${translate('Base.Label.ToDate')}`,
                cellRender: (data: IFaDanThuocItemRepsone) => {
                    return <>
                        {moment(data.den_ngay).format("DD/MM/YYYY")}
                    </>
                }
            },
            {
                id: "noi_dung",
                width: "250px",
                caption: `${translate('Base.Label.Content')}`,
                cellRender: (data: IFaDanThuocItemRepsone) => {
                    return (
                        <Box>
                            {data.chiTiets.map((x, idx) => {
                                return (
                                    <Box key={x.id} sx={{
                                        mb: 1
                                    }}>
                                        <Box sx={{
                                            display: "flex",
                                            flexWrap: "wrap",
                                            alignItems: "center"
                                        }}>1
                                            <Box sx={{
                                                fontWeight: "600"
                                            }}>{idx + 1}. {x.ten_thuoc}</Box>
                                            <Box sx={{
                                                fontSize: "12px",
                                                color: "fg.muted",
                                                ml: 3
                                            }}>{x.cachdung_lieuluong}</Box>
                                        </Box>
                                        <Box sx={{
                                            fontSize: "12px",
                                            color: "fg.muted"
                                        }}>{x.ghi_chu}</Box>
                                    </Box>
                                );
                            })}
                        </Box>
                    );
                }
            },
            {
                id: "hinh_anh",
                width: "100px",
                caption: `${translate('Base.Label.Image')}`,
                cellRender: (data: IFaDanThuocItemRepsone) => {
                    return (
                        <Box sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            justifyContent: "center",
                            alignItems: "center",
                            minHeight: "60px",
                            height: "100%"
                        }}>
                            {data.hinhAnhs.filter((x: any) => x?.url_hinh_anh).map((x, idx) => {
                                return (
                                    <Box key={x.id} sx={{
                                        mb: 2,
                                        mr: 2
                                    }}>
                                        <Box
                                            onClick={() => setPreviewImage(x.url_hinh_anh)}
                                            sx={{ cursor: "pointer" }}
                                        >
                                            <img src={x.url_hinh_anh} alt='Image'
                                                style={{
                                                    height: "50px",
                                                    width: "auto",
                                                    borderRadius: "2px"
                                                }}

                                            />
                                        </Box>

                                    </Box>
                                );
                            })}
                        </Box>
                    );
                }
            },
            {
                dataField: "ghi_chu",
                width: "150px",
                caption: `${translate('Base.Label.Note')}`,
                cellRender: (data: IFaDanThuocItemRepsone) => {
                    return (
                        <Box sx={{
                            fontSize: "12px",
                            color: "fg.muted"
                        }}>
                            {data.ghi_chu}
                        </Box>
                    );
                }
            },



        ];

        return result;
    }, []);

    return (
        <Box sx={{
            p: 3
        }}>

            <Box>
                <DataTable
                    title='Dặn thuốc'
                    subTitle={`${translate("Base.Label.TotalCount")}: ${(dataSource?.length ?? 0).toLocaleString()}`}
                    searchEnable
                    isLoading={isLoading}
                    columns={columns}
                    data={dataSource}
                    height={`${window.innerHeight - 230}px`}
                    fixedLeftColumns={3}
                    paging={{
                        enable: true,
                        pageSizeItems: [20, 50, 200, 500, 1000],
                    }}
                    actionComponent={
                        <Box
                            sx={{
                                display: "flex",
                                gap: 2,
                                alignItems: "flex-end",
                                flexWrap: "wrap"
                            }}
                        >
                            {/* Date filters */}
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, marginRight: "10px" }}>
                                <Text text="Base.Label.FromDate" sx={{ minWidth: "60px", fontSize: "14px" }} />
                                <TextInput
                                    type="date"
                                    value={filterTuNgay}
                                    onChange={(e) => setFilterTuNgay(e.target.value)}
                                    sx={{ minWidth: "150px" }}
                                />
                            </Box>

                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                <Text text="Base.Label.ToDate" sx={{ minWidth: "70px", fontSize: "14px" }} />
                                <TextInput
                                    type="date"
                                    value={filterDenNgay}
                                    onChange={(e) => setFilterDenNgay(e.target.value)}
                                    sx={{ minWidth: "150px" }}
                                />
                            </Box>

                            <Button
                                text="Base.Label.Refresh"
                                leadingVisual={SyncIcon}
                                size="medium"
                                onClick={() => {
                                    handleReloadAsync();
                                }}
                            />


                        </Box>
                    }
                />
            </Box>
            {danThuocIdReject > 0 &&
                <Modal
                    isOpen={true}
                    title="Base.Label.Reject"
                    onClose={() => {
                        setDanThuocIdReject(0)
                    }}
                    width={"large"}
                >
                    <Box>
                        <FormControl>
                            <FormControl.Label>
                                <Text text='Base.Label.Note' />
                            </FormControl.Label>
                            <TextInput block
                                onChange={(e) => {
                                    setDanThuocNote(e.target.value)
                                }}
                            />
                        </FormControl>
                    </Box>
                    <ModalActions>
                        <Button text='Base.Label.Close' onClick={() => {
                            setDanThuocIdReject(0)
                        }} />
                        <Button text='Base.Label.Reject'
                            variant='danger'
                            onClick={() => {
                                submitAcceptAsync(danThuocIdReject, false, danThuocNote)
                            }} />
                    </ModalActions>
                </Modal>
            }

            {/* Image Preview Modal */}
            {previewImage && (
                <Modal
                    isOpen={true}
                    onClose={() => setPreviewImage(null)}
                    title="Base.Label.Image"
                    width="xlarge"
                >
                    <Box sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        p: 3
                    }}>
                        <img
                            src={previewImage}
                            alt="Preview"
                            style={{
                                maxWidth: "100%",
                                maxHeight: "70vh",
                                objectFit: "contain",
                                borderRadius: "4px"
                            }}
                        />
                    </Box>
                </Modal>
            )}
        </Box>
    );
};

export default DanThuocPage;